import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import { BACKEND_URL } from '../config';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Badge } from './ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from './ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from './ui/select';
import {
  Receipt, Plus, Trash2, Edit3, ArrowLeft, CheckCircle2, XCircle,
  ExternalLink, FileText, Wallet,
} from 'lucide-react';
import { toast } from 'sonner';

const STATUS_BADGE = {
  submitted: 'bg-amber-100 text-amber-800 border-amber-200',
  approved:  'bg-emerald-100 text-emerald-800 border-emerald-200',
  rejected:  'bg-rose-100 text-rose-800 border-rose-200',
};

const CATEGORIES = [
  'Travel', 'Accommodation', 'Meals', 'Office Supplies', 'Materials',
  'Software & Tools', 'Training', 'Communications', 'Other',
];

const Reimbursements = ({ user }) => {
  const headers = { Authorization: `Bearer ${localStorage.getItem('token')}` };

  const [items, setItems] = useState([]);
  const [projects, setProjects] = useState([]);
  const [summary, setSummary] = useState({
    by_status: { submitted: 0, approved: 0, rejected: 0 },
    amount_pending: 0, amount_approved: 0, total: 0,
  });
  const [tab, setTab] = useState('pending');
  const [filterProject, setFilterProject] = useState('all');
  const [loading, setLoading] = useState(true);

  // Dialog state
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({
    project_id: '', category: 'Travel', amount: '', currency: 'EUR',
    expense_date: '', description: '', receipt_url: '',
  });
  const [saving, setSaving] = useState(false);

  // Decision dialog
  const [decisionDialog, setDecisionDialog] = useState({ open: false, item: null, action: 'approve', notes: '' });

  const isElevated = ['administrator', 'admin', 'project_manager'].includes(user?.role);

  // Per-item: can current user approve/reject?
  const canDecide = (item) => {
    if (item.status !== 'submitted') return false;
    if (user?.role === 'administrator' || user?.role === 'admin') return true;
    const proj = projects.find((p) => p.id === item.project_id);
    if (!proj) return false;
    return proj.project_manager_id === user?.id || proj.owner_id === user?.id;
  };
  // Per-item: can current user edit/delete (own + still submitted, or admin)?
  const canEdit = (item) => {
    if (user?.role === 'administrator' || user?.role === 'admin') return true;
    return item.requester_id === user?.id && item.status === 'submitted';
  };

  const fetchAll = async () => {
    try {
      setLoading(true);
      const [r, p, s] = await Promise.all([
        axios.get(`${BACKEND_URL}/api/reimbursements`, { headers }),
        axios.get(`${BACKEND_URL}/api/projects`, { headers }),
        axios.get(`${BACKEND_URL}/api/reimbursements/summary`, { headers }),
      ]);
      setItems(r.data || []);
      setProjects(p.data || []);
      setSummary(s.data || summary);
    } catch (err) {
      console.error(err);
      toast.error('Failed to load reimbursements');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchAll(); /* eslint-disable-next-line */ }, []);

  const filtered = useMemo(() => {
    let list = items;
    if (filterProject !== 'all') list = list.filter((m) => m.project_id === filterProject);
    if (tab === 'pending') list = list.filter((m) => m.status === 'submitted');
    else if (tab === 'mine') list = list.filter((m) => m.requester_id === user?.id);
    else if (tab === 'approved') list = list.filter((m) => m.status === 'approved');
    else if (tab === 'rejected') list = list.filter((m) => m.status === 'rejected');
    return list;
  }, [items, tab, filterProject, user?.id]);

  const openCreate = () => {
    setEditing(null);
    setForm({
      project_id: projects[0]?.id || '',
      category: 'Travel',
      amount: '',
      currency: 'EUR',
      expense_date: new Date().toISOString().slice(0, 10),
      description: '',
      receipt_url: '',
    });
    setDialogOpen(true);
  };

  const openEdit = (item) => {
    setEditing(item);
    setForm({
      project_id: item.project_id,
      category: item.category || 'Travel',
      amount: String(item.amount || ''),
      currency: item.currency || 'EUR',
      expense_date: (item.expense_date || '').slice(0, 10),
      description: item.description || '',
      receipt_url: item.receipt_url || '',
    });
    setDialogOpen(true);
  };

  const submit = async (e) => {
    e?.preventDefault?.();
    if (!form.project_id) { toast.error('Project is required'); return; }
    if (!form.category) { toast.error('Category is required'); return; }
    const amt = parseFloat(form.amount);
    if (!amt || amt <= 0) { toast.error('Amount must be a positive number'); return; }
    if (!form.expense_date) { toast.error('Expense date is required'); return; }

    setSaving(true);
    try {
      const body = {
        project_id: form.project_id,
        category: form.category,
        amount: amt,
        currency: form.currency || 'EUR',
        expense_date: new Date(form.expense_date).toISOString(),
        description: form.description || '',
        receipt_url: form.receipt_url || null,
      };
      if (editing) {
        await axios.put(`${BACKEND_URL}/api/reimbursements/${editing.id}`, body, { headers });
        toast.success('Reimbursement updated');
      } else {
        await axios.post(`${BACKEND_URL}/api/reimbursements`, body, { headers });
        toast.success('Reimbursement submitted for approval');
      }
      setDialogOpen(false);
      fetchAll();
    } catch (err) {
      console.error(err);
      toast.error(err?.response?.data?.detail || 'Failed to save reimbursement');
    } finally {
      setSaving(false);
    }
  };

  const remove = async (item) => {
    if (!window.confirm(`Delete reimbursement of ${item.currency} ${item.amount} for "${item.category}"?`)) return;
    try {
      await axios.delete(`${BACKEND_URL}/api/reimbursements/${item.id}`, { headers });
      toast.success('Reimbursement deleted');
      fetchAll();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to delete');
    }
  };

  const openDecision = (item, action) => {
    setDecisionDialog({ open: true, item, action, notes: '' });
  };

  const confirmDecision = async () => {
    const { item, action, notes } = decisionDialog;
    try {
      await axios.post(
        `${BACKEND_URL}/api/reimbursements/${item.id}/${action}`,
        { notes: notes || '' },
        { headers }
      );
      toast.success(action === 'approve'
        ? 'Reimbursement approved — expense added to project'
        : 'Reimbursement rejected');
      setDecisionDialog({ open: false, item: null, action: 'approve', notes: '' });
      fetchAll();
    } catch (err) {
      toast.error(err?.response?.data?.detail || `Failed to ${action}`);
    }
  };

  const fmtMoney = (v, c = 'EUR') =>
    `${(c || 'EUR')} ${Number(v || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

  const fmtDate = (iso) => {
    if (!iso) return '—';
    const d = new Date(iso);
    return Number.isNaN(d.getTime()) ? iso : d.toLocaleDateString('en-GB');
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <Link to="/dashboard" className="text-slate-600 hover:text-slate-900">
              <ArrowLeft className="w-5 h-5" />
            </Link>
            <div>
              <h1 className="text-3xl font-bold text-slate-900 flex items-center gap-2" data-testid="reimbursements-title">
                <Receipt className="w-7 h-7 text-emerald-700" /> Reimbursements
              </h1>
              <p className="text-sm text-slate-500 mt-1">
                Submit out-of-pocket expense claims · approved requests are auto-added to project expenses.
              </p>
            </div>
          </div>
          <Button
            onClick={openCreate}
            className="bg-emerald-700 hover:bg-emerald-800"
            data-testid="new-reimbursement-btn"
          >
            <Plus className="w-4 h-4 mr-2" /> New Reimbursement
          </Button>
        </div>

        {/* Summary tiles */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
          <Card data-testid="summary-tile-pending">
            <CardContent className="p-4">
              <p className="text-xs uppercase tracking-wide text-slate-500">Pending</p>
              <p className="text-2xl font-bold text-amber-700 mt-1">{summary.by_status?.submitted || 0}</p>
              <p className="text-xs text-slate-500 mt-1">{fmtMoney(summary.amount_pending)}</p>
            </CardContent>
          </Card>
          <Card data-testid="summary-tile-approved">
            <CardContent className="p-4">
              <p className="text-xs uppercase tracking-wide text-slate-500">Approved</p>
              <p className="text-2xl font-bold text-emerald-700 mt-1">{summary.by_status?.approved || 0}</p>
              <p className="text-xs text-slate-500 mt-1">{fmtMoney(summary.amount_approved)}</p>
            </CardContent>
          </Card>
          <Card data-testid="summary-tile-rejected">
            <CardContent className="p-4">
              <p className="text-xs uppercase tracking-wide text-slate-500">Rejected</p>
              <p className="text-2xl font-bold text-rose-700 mt-1">{summary.by_status?.rejected || 0}</p>
            </CardContent>
          </Card>
          <Card data-testid="summary-tile-total">
            <CardContent className="p-4">
              <p className="text-xs uppercase tracking-wide text-slate-500">Total Requests</p>
              <p className="text-2xl font-bold text-slate-900 mt-1">{summary.total || 0}</p>
            </CardContent>
          </Card>
        </div>

        {/* Filter + Tabs */}
        <Card>
          <CardHeader className="pb-2">
            <div className="flex flex-col md:flex-row md:items-center gap-3 justify-between">
              <CardTitle className="text-lg flex items-center gap-2">
                <Wallet className="w-5 h-5 text-slate-500" /> Requests
              </CardTitle>
              <div className="w-full md:w-72">
                <Select value={filterProject} onValueChange={setFilterProject}>
                  <SelectTrigger data-testid="project-filter">
                    <SelectValue placeholder="All projects" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All projects</SelectItem>
                    {projects.map((p) => (
                      <SelectItem key={p.id} value={p.id}>{p.title}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            <Tabs value={tab} onValueChange={setTab}>
              <TabsList className="mb-3">
                <TabsTrigger value="pending" data-testid="tab-pending">Pending Approval</TabsTrigger>
                <TabsTrigger value="mine" data-testid="tab-mine">My Submissions</TabsTrigger>
                <TabsTrigger value="approved" data-testid="tab-approved">Approved</TabsTrigger>
                <TabsTrigger value="rejected" data-testid="tab-rejected">Rejected</TabsTrigger>
                <TabsTrigger value="all" data-testid="tab-all">All</TabsTrigger>
              </TabsList>
              <TabsContent value={tab}>
                {loading ? (
                  <div className="py-10 text-center text-slate-500">Loading…</div>
                ) : filtered.length === 0 ? (
                  <div className="py-12 text-center text-slate-500">
                    <FileText className="w-10 h-10 mx-auto mb-2 text-slate-300" />
                    <p>No reimbursements in this view.</p>
                    <Button onClick={openCreate} variant="outline" className="mt-3" size="sm">
                      <Plus className="w-4 h-4 mr-2" /> Submit your first request
                    </Button>
                  </div>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm" data-testid="reimbursements-table">
                      <thead>
                        <tr className="text-left border-b border-slate-200 text-slate-500 text-xs uppercase tracking-wide">
                          <th className="py-2 pr-3">Date</th>
                          <th className="py-2 pr-3">Project</th>
                          <th className="py-2 pr-3">Category</th>
                          <th className="py-2 pr-3">Description</th>
                          <th className="py-2 pr-3 text-right">Amount</th>
                          <th className="py-2 pr-3">Requester</th>
                          <th className="py-2 pr-3">Status</th>
                          <th className="py-2 pr-3">Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {filtered.map((it) => (
                          <tr key={it.id} className="border-b border-slate-100 hover:bg-slate-50" data-testid={`row-${it.id}`}>
                            <td className="py-3 pr-3 whitespace-nowrap">{fmtDate(it.expense_date)}</td>
                            <td className="py-3 pr-3 max-w-[220px] truncate">
                              <Link to={`/project/${it.project_id}`} className="text-emerald-700 hover:underline">
                                {it.project_title || it.project_id?.slice(0, 8)}
                              </Link>
                            </td>
                            <td className="py-3 pr-3">{it.category}</td>
                            <td className="py-3 pr-3 max-w-[260px]">
                              <p className="truncate text-slate-700" title={it.description || '—'}>
                                {it.description || <span className="text-slate-500 italic">—</span>}
                              </p>
                            </td>
                            <td className="py-3 pr-3 text-right font-mono">{fmtMoney(it.amount, it.currency)}</td>
                            <td className="py-3 pr-3">{it.requester_name || '—'}</td>
                            <td className="py-3 pr-3">
                              <Badge className={`${STATUS_BADGE[it.status]} border`} data-testid={`status-${it.id}`}>
                                {it.status}
                              </Badge>
                              {it.review_notes && (
                                <p className="text-xs text-slate-500 mt-1 max-w-[200px] truncate" title={it.review_notes}>
                                  Note: {it.review_notes}
                                </p>
                              )}
                            </td>
                            <td className="py-3 pr-3">
                              <div className="flex items-center gap-1 flex-wrap">
                                {canDecide(it) && (
                                  <>
                                    <Button
                                      size="sm" variant="outline"
                                      className="h-7 text-emerald-700 border-emerald-300 hover:bg-emerald-50"
                                      onClick={() => openDecision(it, 'approve')}
                                      data-testid={`approve-${it.id}`}
                                    >
                                      <CheckCircle2 className="w-3.5 h-3.5 mr-1" /> Approve
                                    </Button>
                                    <Button
                                      size="sm" variant="outline"
                                      className="h-7 text-rose-700 border-rose-300 hover:bg-rose-50"
                                      onClick={() => openDecision(it, 'reject')}
                                      data-testid={`reject-${it.id}`}
                                    >
                                      <XCircle className="w-3.5 h-3.5 mr-1" /> Reject
                                    </Button>
                                  </>
                                )}
                                {it.receipt_url && (
                                  <a
                                    href={it.receipt_url}
                                    target="_blank" rel="noreferrer"
                                    className="inline-flex items-center text-xs text-slate-500 hover:text-slate-800 px-2"
                                  >
                                    <ExternalLink className="w-3.5 h-3.5 mr-1" /> Receipt
                                  </a>
                                )}
                                {canEdit(it) && (
                                  <>
                                    <Button
                                      size="sm" variant="ghost" className="h-7 px-2"
                                      onClick={() => openEdit(it)}
                                      data-testid={`edit-${it.id}`}
                                    >
                                      <Edit3 className="w-3.5 h-3.5" />
                                    </Button>
                                    <Button
                                      size="sm" variant="ghost" className="h-7 px-2 text-rose-600 hover:text-rose-700"
                                      onClick={() => remove(it)}
                                      data-testid={`delete-${it.id}`}
                                    >
                                      <Trash2 className="w-3.5 h-3.5" />
                                    </Button>
                                  </>
                                )}
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </TabsContent>
            </Tabs>
          </CardContent>
        </Card>
      </div>

      {/* Create / Edit dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-xl" data-testid="reimbursement-dialog">
          <DialogHeader>
            <DialogTitle>{editing ? 'Edit Reimbursement' : 'New Reimbursement Request'}</DialogTitle>
            <DialogDescription>
              {editing
                ? 'Update the details of this submitted reimbursement request.'
                : 'Submit an out-of-pocket expense for review. Once approved, it will be auto-recorded as a project expense.'}
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={submit} className="space-y-4">
            <div>
              <Label>Project *</Label>
              <Select
                value={form.project_id}
                onValueChange={(v) => setForm((f) => ({ ...f, project_id: v }))}
              >
                <SelectTrigger data-testid="form-project"><SelectValue placeholder="Select project…" /></SelectTrigger>
                <SelectContent>
                  {projects.map((p) => (
                    <SelectItem key={p.id} value={p.id}>{p.title}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Category *</Label>
                <Select
                  value={form.category}
                  onValueChange={(v) => setForm((f) => ({ ...f, category: v }))}
                >
                  <SelectTrigger data-testid="form-category"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {CATEGORIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Expense Date *</Label>
                <Input
                  type="date" required
                  value={form.expense_date}
                  onChange={(e) => setForm((f) => ({ ...f, expense_date: e.target.value }))}
                  data-testid="form-date"
                />
              </div>
            </div>
            <div className="grid grid-cols-3 gap-3">
              <div className="col-span-2">
                <Label>Amount *</Label>
                <Input
                  type="number" step="0.01" min="0.01" required
                  value={form.amount}
                  onChange={(e) => setForm((f) => ({ ...f, amount: e.target.value }))}
                  placeholder="0.00"
                  data-testid="form-amount"
                />
              </div>
              <div>
                <Label>Currency</Label>
                <Select
                  value={form.currency}
                  onValueChange={(v) => setForm((f) => ({ ...f, currency: v }))}
                >
                  <SelectTrigger data-testid="form-currency"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="EUR">EUR</SelectItem>
                    <SelectItem value="USD">USD</SelectItem>
                    <SelectItem value="GBP">GBP</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div>
              <Label>Receipt URL (optional)</Label>
              <Input
                type="url"
                placeholder="https://… (link to uploaded receipt)"
                value={form.receipt_url}
                onChange={(e) => setForm((f) => ({ ...f, receipt_url: e.target.value }))}
                data-testid="form-receipt"
              />
            </div>
            <div>
              <Label>Description</Label>
              <Textarea
                rows={3}
                placeholder="Describe what this expense covers…"
                value={form.description}
                onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))}
                data-testid="form-description"
              />
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>Cancel</Button>
              <Button
                type="submit"
                disabled={saving}
                className="bg-emerald-700 hover:bg-emerald-800"
                data-testid="form-submit"
              >
                {saving ? 'Saving…' : (editing ? 'Save changes' : 'Submit request')}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Decision dialog */}
      <Dialog
        open={decisionDialog.open}
        onOpenChange={(o) => setDecisionDialog((d) => ({ ...d, open: o }))}
      >
        <DialogContent className="max-w-md" data-testid="decision-dialog">
          <DialogHeader>
            <DialogTitle>
              {decisionDialog.action === 'approve' ? 'Approve reimbursement' : 'Reject reimbursement'}
            </DialogTitle>
            <DialogDescription>
              {decisionDialog.action === 'approve'
                ? 'Confirm approval. An expense entry will be auto-created on the project.'
                : 'Please provide a brief reason that will be visible to the requester.'}
            </DialogDescription>
          </DialogHeader>
          {decisionDialog.item && (
            <div className="space-y-3">
              <div className="text-sm text-slate-600">
                <p><span className="font-medium">Project:</span> {decisionDialog.item.project_title}</p>
                <p><span className="font-medium">Requester:</span> {decisionDialog.item.requester_name}</p>
                <p><span className="font-medium">Amount:</span> {fmtMoney(decisionDialog.item.amount, decisionDialog.item.currency)}</p>
                <p><span className="font-medium">Category:</span> {decisionDialog.item.category}</p>
                {decisionDialog.item.description && (
                  <p className="mt-2 text-slate-500 italic">"{decisionDialog.item.description}"</p>
                )}
              </div>
              {decisionDialog.action === 'approve' && (
                <div className="bg-emerald-50 border border-emerald-200 rounded p-3 text-xs text-emerald-800">
                  Approving this request will automatically create a matching expense in the project so its budget tracking stays accurate.
                </div>
              )}
              <div>
                <Label>{decisionDialog.action === 'approve' ? 'Notes (optional)' : 'Reason'}</Label>
                <Textarea
                  rows={3}
                  placeholder={decisionDialog.action === 'approve' ? 'Optional approval note…' : 'Tell the requester why…'}
                  value={decisionDialog.notes}
                  onChange={(e) => setDecisionDialog((d) => ({ ...d, notes: e.target.value }))}
                  data-testid="decision-notes"
                />
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setDecisionDialog({ open: false, item: null, action: 'approve', notes: '' })}>
              Cancel
            </Button>
            <Button
              onClick={confirmDecision}
              className={decisionDialog.action === 'approve'
                ? 'bg-emerald-700 hover:bg-emerald-800'
                : 'bg-rose-600 hover:bg-rose-700'}
              data-testid="decision-confirm"
            >
              {decisionDialog.action === 'approve' ? 'Approve & create expense' : 'Reject request'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Reimbursements;
